// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Inline email-verification control that sits directly under an email input.
 * The parent owns the email value; this component drives the request/confirm
 * OTP round-trip and reports the verified state back via onVerifiedChange.
 *
 * variant: "dark" (registration screen) | "light" (talent pool form)
 */
export function EmailVerify({
  email,
  purpose = "register",
  verified,
  onVerifiedChange,
  variant = "dark",
  onAccountExists,
}) {
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  const emailValid = EMAIL_RE.test(String(email || "").trim());

  // Reset the whole flow whenever the email changes (parent already flips
  // `verified` to false, but we also clear any sent-code UI + errors here).
  useEffect(() => {
    setCodeSent(false);
    setCode("");
    setErr("");
    setInfo("");
    setCooldown(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    return () => timerRef.current && clearInterval(timerRef.current);
  }, []);

  const startCooldown = (secs) => {
    setCooldown(secs);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const sendCode = async () => {
    setErr("");
    setInfo("");
    if (!emailValid) {
      setErr("Enter a valid email first.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/auth/verify-email/request", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: String(email).trim(), purpose }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429 && data.retryAfterSeconds) {
        startCooldown(Number(data.retryAfterSeconds));
      }
      if (!res.ok) {
        const msg = data.error || "Could not send the code. Try again.";
        setErr(msg);
        if (
          res.status === 409 ||
          data.code === "account_exists"
        ) {
          onAccountExists?.(String(email).trim());
        }
        return;
      }
      setCodeSent(true);
      setInfo(`Code sent to ${String(email).trim()}. Check your inbox.`);
      startCooldown(60);
    } catch (e) {
      setErr("Cannot reach server. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async () => {
    setErr("");
    setInfo("");
    if (!/^\d{6}$/.test(code.trim())) {
      setErr("Enter the 6-digit code from your email.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/auth/verify-email/confirm", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: String(email).trim(), code: code.trim(), purpose }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(data.error || "Could not verify. Try again.");
        return;
      }
      onVerifiedChange(true);
      setCodeSent(false);
    } catch (e) {
      setErr("Cannot reach server. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const dark = variant !== "light";
  const linkCls = dark
    ? "text-brand-400 hover:text-brand-300"
    : "text-brand-600 hover:text-brand-500";
  const codeInputCls = dark
    ? "flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white tracking-[0.3em] text-center font-bold placeholder-slate-600 focus:outline-none focus:border-brand-500"
    : "flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 tracking-[0.3em] text-center font-bold placeholder-slate-300 focus:outline-none focus:border-brand-400";
  const confirmBtnCls = dark
    ? "px-4 py-2 bg-brand-600 hover:bg-brand-500 disabled:bg-slate-700 text-white text-sm font-bold rounded-lg"
    : "px-4 py-2 bg-brand-600 hover:bg-brand-500 disabled:bg-slate-200 text-white text-sm font-bold rounded-lg";

  if (verified) {
    return (
      <p className="mt-1.5 text-xs font-bold text-emerald-500 flex items-center gap-1">
        <span>✓</span> Email verified
      </p>
    );
  }

  return (
    <div className="mt-1.5">
      {!codeSent && (
        <button
          type="button"
          onClick={sendCode}
          disabled={busy || !emailValid || cooldown > 0}
          className={`text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed ${linkCls}`}
        >
          {busy
            ? "Sending code…"
            : cooldown > 0
              ? `Resend in ${cooldown}s`
              : "Verify email →"}
        </button>
      )}

      {codeSent && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); setErr(""); }}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), confirmCode())}
              placeholder="123456"
              className={codeInputCls}
            />
            <button
              type="button"
              onClick={confirmCode}
              disabled={busy || code.length !== 6}
              className={confirmBtnCls}
            >
              {busy ? "Verifying…" : "Verify"}
            </button>
          </div>
          <button
            type="button"
            onClick={sendCode}
            disabled={busy || cooldown > 0}
            className={`text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed ${linkCls}`}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
        </div>
      )}

      {info && !err && <p className="mt-1 text-xs text-slate-400">{info}</p>}
      {err && <p className="mt-1 text-xs text-red-400">⚠ {err}</p>}
    </div>
  );
}
